import { Bell, Download, Share, SquarePlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	isSubscribed,
	pushPermission,
	pushSupported,
	subscribePush,
} from "../lib/push.ts";
import { notifyError, notifySuccess } from "../lib/toast.ts";

const DISMISS_KEY = "klyra.install-dismissed";
const PUSH_DISMISS_KEY = "klyra.push-nudge-dismissed";

type BeforeInstallPromptEvent = Event & {
	prompt: () => void;
	userChoice: Promise<{ outcome: string }>;
};

function isStandalone(): boolean {
	return (
		window.matchMedia("(display-mode: standalone)").matches ||
		(navigator as unknown as { standalone?: boolean }).standalone === true
	);
}

/**
 * Mobile install + post-install push activation.
 *
 * Three mutually-exclusive states, in priority order:
 *   1. Already installed (standalone) + notifications still off  → a push
 *      activation nudge. This is the real payoff of installing — especially
 *      on iOS, where web push ONLY works once the app is on the home screen.
 *   2. Android/Chromium, installable → a one-tap install button (captured
 *      `beforeinstallprompt`).
 *   3. iOS Safari, not installed → clear illustrated 3-step Add-to-Home-Screen
 *      guide (iOS has no programmatic install).
 *
 * Each is dismissible and remembered, so nothing nags. Staff-app + mobile only.
 */
export function InstallPrompt() {
	const { t } = useTranslation();
	const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
		null,
	);
	const [mode, setMode] = useState<"android" | "ios" | "push" | null>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let cancelled = false;

		// ----- Installed already → maybe nudge for notifications -----
		if (isStandalone()) {
			(async () => {
				try {
					if (localStorage.getItem(PUSH_DISMISS_KEY) === "1") return;
				} catch {
					/* ignore */
				}
				if (!pushSupported() || pushPermission() !== "default") return;
				const subbed = await isSubscribed().catch(() => false);
				if (!cancelled && !subbed) setMode("push");
			})();
			return () => {
				cancelled = true;
			};
		}

		// ----- Not installed → install guidance -----
		try {
			if (localStorage.getItem(DISMISS_KEY) === "1") return;
		} catch {
			/* ignore */
		}

		const ua = navigator.userAgent;
		if (/iphone|ipad|ipod/i.test(ua)) {
			const iosSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
			if (iosSafari) setMode("ios");
			return;
		}

		const handler = (e: Event) => {
			e.preventDefault();
			setDeferred(e as BeforeInstallPromptEvent);
			setMode("android");
		};
		window.addEventListener("beforeinstallprompt", handler);
		return () => {
			cancelled = true;
			window.removeEventListener("beforeinstallprompt", handler);
		};
	}, []);

	if (!mode) return null;

	const dismissInstall = () => {
		setMode(null);
		try {
			localStorage.setItem(DISMISS_KEY, "1");
		} catch {
			/* ignore */
		}
	};

	const dismissPush = () => {
		setMode(null);
		try {
			localStorage.setItem(PUSH_DISMISS_KEY, "1");
		} catch {
			/* ignore */
		}
	};

	const install = async () => {
		if (!deferred) return;
		deferred.prompt();
		await deferred.userChoice.catch(() => undefined);
		setDeferred(null);
		dismissInstall();
	};

	const enablePush = async () => {
		setBusy(true);
		try {
			const ok = await subscribePush();
			if (ok) {
				notifySuccess(
					t("push.enabled", { defaultValue: "تم تفعيل الإشعارات" }),
				);
				setMode(null);
			} else {
				notifyError(null, t("push.blocked"));
			}
		} catch (err) {
			notifyError(err, t("push.blocked"));
		} finally {
			setBusy(false);
		}
	};

	const Kmark = (
		<div className="grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-brand text-sm font-black text-white">
			K
		</div>
	);

	// ---------- Push activation nudge (installed) ----------
	if (mode === "push") {
		return (
			<div className="sm:hidden mx-3 mt-3 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
				<div className="grid size-9 shrink-0 place-items-center rounded-lg bg-emerald-500 text-white">
					<Bell className="size-5" strokeWidth={2} aria-hidden />
				</div>
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
						{t("push.nudge_title", { defaultValue: "فعّل إشعارات العيادة" })}
					</p>
					<p className="text-xs text-emerald-700/80 dark:text-emerald-300/70">
						{t("push.nudge_body", {
							defaultValue: "عشان توصلك التحويلات والطلبات والمواعيد فوراً.",
						})}
					</p>
				</div>
				<button
					type="button"
					onClick={enablePush}
					disabled={busy}
					className="shrink-0 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
				>
					{busy ? "…" : t("push.enable")}
				</button>
				<button
					type="button"
					onClick={dismissPush}
					aria-label={t("actions.close")}
					className="shrink-0 text-muted-foreground hover:text-muted-foreground"
				>
					<X className="size-4" strokeWidth={2} aria-hidden />
				</button>
			</div>
		);
	}

	// ---------- iOS: illustrated Add-to-Home-Screen steps ----------
	if (mode === "ios") {
		return (
			<div className="sm:hidden mx-3 mt-3 rounded-xl border border-brand-200 bg-brand-50 p-3 dark:border-brand-900 dark:bg-brand-950/40">
				<div className="flex items-start gap-3">
					{Kmark}
					<p className="min-w-0 flex-1 text-sm font-medium text-brand-900 dark:text-brand-200">
						{t("install.ios_title", { defaultValue: "ثبّت كليرا على آيفون" })}
					</p>
					<button
						type="button"
						onClick={dismissInstall}
						aria-label={t("actions.close")}
						className="shrink-0 text-muted-foreground hover:text-muted-foreground"
					>
						<X className="size-4" strokeWidth={2} aria-hidden />
					</button>
				</div>
				<ol className="mt-2.5 flex flex-col gap-2 ps-1">
					<Step
						n={1}
						icon={<Share className="size-4" strokeWidth={2} aria-hidden />}
					>
						{t("install.ios_step1", {
							defaultValue: "اضغط زر المشاركة في شريط سفاري",
						})}
					</Step>
					<Step
						n={2}
						icon={<SquarePlus className="size-4" strokeWidth={2} aria-hidden />}
					>
						{t("install.ios_step2", {
							defaultValue: "اختر «إضافة إلى الشاشة الرئيسية»",
						})}
					</Step>
					<Step n={3} icon={<span className="text-xs font-black">K</span>}>
						{t("install.ios_step3", {
							defaultValue: "افتح كليرا من الأيقونة الجديدة",
						})}
					</Step>
				</ol>
			</div>
		);
	}

	// ---------- Android/Chromium: one-tap install ----------
	return (
		<div className="sm:hidden mx-3 mt-3 flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 p-3 dark:border-brand-900 dark:bg-brand-950/40">
			{Kmark}
			<p className="min-w-0 flex-1 text-sm text-brand-900 dark:text-brand-200">
				{t("install.title")}
			</p>
			<button
				type="button"
				onClick={install}
				className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
			>
				<Download className="size-4" strokeWidth={2} aria-hidden />
				{t("install.action")}
			</button>
			<button
				type="button"
				onClick={dismissInstall}
				aria-label={t("actions.close")}
				className="shrink-0 text-muted-foreground hover:text-muted-foreground"
			>
				<X className="size-4" strokeWidth={2} aria-hidden />
			</button>
		</div>
	);
}

function Step({
	n,
	icon,
	children,
}: { n: number; icon: React.ReactNode; children: React.ReactNode }) {
	return (
		<li className="flex items-center gap-2.5 text-xs text-brand-900 dark:text-brand-200">
			<span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 dark:bg-brand-900/60 dark:text-brand-300 tabular-nums">
				{n}
			</span>
			<span className="grid size-6 shrink-0 place-items-center rounded-md bg-card text-brand-600 dark:text-brand-300">
				{icon}
			</span>
			<span className="min-w-0">{children}</span>
		</li>
	);
}
